import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ChevronDown, ChevronUp, FileDown, ScanLine } from "lucide-react";
import { api, errCode, download } from "../lib/api";
import { useLang } from "../lib/i18n";
import { todayCL, fmtTime, kg } from "../lib/time";
import { PageTitle } from "../components/Common";
import { useMaster, nameOf } from "../lib/master";

export const GuideButton = ({ d }) => {
  const { t, te } = useLang();
  return (
    <button className="btn-secondary btn-sm" onClick={(e) => { e.stopPropagation(); download(`/dispatches/${d.id}/guide.pdf`, `guia-${d.number}.pdf`).catch((x) => toast.error(te(errCode(x)))); }}
      data-testid={`guide-pdf-${d.number}`}><FileDown size={14} />{t("ph_guide")}</button>
  );
};

const Detail = ({ id }) => {
  const { t } = useLang();
  const [g, setG] = useState(null);
  useEffect(() => { api.get(`/dispatches/${id}`).then((r) => setG(r.data)); }, [id]);
  if (!g) return <p className="p-3">{t("loading")}</p>;
  return (
    <div className="p-3 space-y-3 bg-slate-50" data-testid={`dispatch-detail-${g.number}`}>
      <div className="flex flex-wrap gap-2">{g.groups.map((x) => (
        <span key={`${x.csg}-${x.variety}`} className="text-xs font-bold bg-white border rounded-lg px-2 py-1">CSG {x.csg} · {x.variety} · {x.bins} bins · {kg(x.net_kg)}</span>))}</div>
      <table className="tbl w-full bg-white"><thead><tr><th>{t("label")}</th><th>{t("sector")}</th><th>CSG</th><th>{t("variety")}</th><th>{t("ph_dock_net")}</th><th>{t("ph_net")}</th><th>{t("ph_diff")}</th></tr></thead>
        <tbody>{g.bins.map((b) => {
          const diff = b.ph_net_kg != null && b.net_kg != null ? Math.round((b.ph_net_kg - b.net_kg) * 10) / 10 : null;
          return (
            <tr key={b.id} data-testid={`incoming-bin-${b.label}`}>
              <td><Link to={`/bin/${b.id}`} className="font-mono font-bold text-[#800B28] underline">{b.label}</Link></td>
              <td className="text-sm">{b.sector}</td><td className="text-sm">{b.csg}</td><td className="text-sm">{b.variety}</td>
              <td className="font-mono">{kg(b.net_kg)}</td><td className="font-mono">{b.ph_net_kg != null ? kg(b.ph_net_kg) : "—"}</td>
              <td className={`font-mono ${diff != null && diff < 0 ? "text-red-700" : ""}`}>{diff != null ? `${diff > 0 ? "+" : ""}${diff}` : "—"}</td>
            </tr>);
        })}</tbody></table>
    </div>
  );
};

export default function Incoming() {
  const { t, te, lang } = useLang();
  const [date, setDate] = useState(todayCL());
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(null);
  const farms = useMaster("farms");
  useEffect(() => { api.get("/dispatches", { params: { date } }).then((r) => setRows(r.data)).catch((e) => toast.error(te(errCode(e)))); }, [date, te]);
  const tot = rows.reduce((s, d) => ({ bins: s.bins + d.bin_count, kg: s.kg + d.net_kg, rec: s.rec + d.received }), { bins: 0, kg: 0, rec: 0 });

  return (
    <div className="space-y-5" data-testid="incoming-page">
      <PageTitle right={<Link to="/scan?mode=ph" className="btn-primary btn-sm" data-testid="incoming-scan-button"><ScanLine size={16} />{t("ph_scan")}</Link>}>{t("ph_title")}</PageTitle>
      <div className="flex flex-wrap items-end gap-4">
        <label><span className="eyebrow">{t("date")}</span><input type="date" className="field mt-1" value={date} max={todayCL()} onChange={(e) => setDate(e.target.value)} data-testid="incoming-date" /></label>
        <p className="text-sm font-semibold" data-testid="incoming-totals">{rows.length} {t("ph_trucks")} · {tot.bins} bins · {kg(tot.kg)} · {t("ph_received")}: {tot.rec}/{tot.bins}</p>
      </div>
      <p className="text-xs text-slate-500">{t("ph_hint")}</p>
      <div className="card overflow-x-auto">
        <table className="tbl w-full" data-testid="incoming-table">
          <thead><tr><th></th><th>{t("ph_guide")}</th><th>{t("dp_time")}</th><th>{t("farm")}</th><th>{t("dp_driver")}</th><th>{t("dp_plate")}</th><th>Bins</th><th>{t("ph_dock_net")}</th><th>{t("ph_received")}</th><th></th></tr></thead>
          <tbody>
            {!rows.length && <tr><td colSpan={10} className="text-slate-500">{t("none")}</td></tr>}
            {rows.map((d) => [
              <tr key={d.id} className="cursor-pointer" onClick={() => setOpen(open === d.id ? null : d.id)} data-testid={`incoming-row-${d.number}`}>
                <td>{open === d.id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}</td>
                <td className="font-mono font-bold">{d.number}</td><td className="font-mono text-xs">{fmtTime(d.dispatched_at, lang)}</td>
                <td className="text-sm font-semibold">{nameOf(farms, d.farm_id)}</td><td>{d.driver_name}</td><td className="font-mono">{d.truck_plate}</td>
                <td className="font-mono">{d.bin_count}</td><td className="font-mono">{kg(d.net_kg)}</td>
                <td><span className={`text-xs font-bold px-2 py-0.5 rounded ${d.received === d.bin_count ? "bg-emerald-100 text-emerald-800" : d.received ? "bg-amber-100 text-amber-900" : "bg-slate-100 text-slate-600"}`}>{d.received}/{d.bin_count}</span></td>
                <td><GuideButton d={d} /></td>
              </tr>,
              open === d.id && <tr key={`${d.id}-x`}><td colSpan={10} className="!p-0"><Detail id={d.id} /></td></tr>,
            ])}
          </tbody>
        </table>
      </div>
    </div>
  );
}
